import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const QuickTest = () => {
  const [timeLeft, setTimeLeft] = useState(60);
  const [isActive, setIsActive] = useState(false);
  const [questions, setQuestions] = useState<Array<{num1: number, num2: number, answer: number}>>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [results, setResults] = useState<Array<{question: string, userAnswer: number, correct: boolean}>>([]);

  const generateRandomQuestion = () => {
    const num1 = Math.floor(Math.random() * 12) + 1;
    const num2 = Math.floor(Math.random() * 12) + 1;
    return {
      num1,
      num2,
      answer: num1 * num2
    };
  };

  const startTest = () => {
    const newQuestions = Array.from({ length: 20 }, generateRandomQuestion);
    setQuestions(newQuestions);
    setCurrentQuestion(0);
    setScore(0);
    setTimeLeft(60);
    setIsActive(true);
    setCompleted(false);
    setResults([]);
    setUserAnswer("");
  };

  const submitAnswer = () => {
    if (!userAnswer) return;
    
    const current = questions[currentQuestion];
    const userNum = parseInt(userAnswer);
    const correct = userNum === current.answer;
    
    setResults(prev => [...prev, {
      question: `${current.num1} × ${current.num2}`,
      userAnswer: userNum,
      correct
    }]);

    if (correct) {
      setScore(score + 1);
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setUserAnswer("");
    } else {
      endTest();
    }
  };

  const endTest = () => {
    setIsActive(false);
    setCompleted(true);
  };

  const skipQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setResults(prev => [...prev, {
        question: `${questions[currentQuestion].num1} × ${questions[currentQuestion].num2}`,
        userAnswer: 0,
        correct: false
      }]);
      setCurrentQuestion(currentQuestion + 1);
      setUserAnswer("");
    } else {
      endTest();
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      endTest();
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, timeLeft]);

  const currentQ = questions[currentQuestion];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Uji Cepat Perkalian</CardTitle>
          <CardDescription>
            Jawab sebanyak mungkin soal perkalian dalam 60 detik!
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isActive && !completed ? (
            <div className="text-center space-y-4">
              <div className="text-lg">
                Siap untuk tantangan? Anda akan mendapat 20 soal perkalian acak.
              </div>
              <div className="text-sm text-muted-foreground">
                Klik tombol di bawah untuk memulai timer 60 detik!
              </div>
              <Button onClick={startTest} size="lg" className="w-full">
                Mulai Uji Cepat
              </Button>
            </div>
          ) : isActive ? (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <Badge variant={timeLeft > 20 ? "default" : "destructive"}>
                  ⏰ {timeLeft}s
                </Badge>
                <Badge variant="secondary">
                  Soal {currentQuestion + 1}/20
                </Badge>
                <Badge variant="outline">
                  Skor: {score}
                </Badge>
              </div>

              <Progress value={((currentQuestion + 1) / 20) * 100} className="h-2" />

              {currentQ && (
                <Card className="bg-gradient-to-r from-primary/10 to-secondary/10">
                  <CardContent className="pt-6">
                    <div className="text-center space-y-4">
                      <div className="text-4xl font-bold">
                        {currentQ.num1} × {currentQ.num2} = ?
                      </div>
                      
                      <Input
                        type="number"
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        placeholder="Jawaban"
                        className="text-center text-xl"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            submitAnswer();
                          }
                        }}
                        autoFocus
                      />
                      
                      <div className="flex gap-2">
                        <Button 
                          onClick={submitAnswer} 
                          disabled={!userAnswer}
                          className="flex-1"
                        >
                          Submit
                        </Button>
                        <Button 
                          onClick={skipQuestion} 
                          variant="outline"
                          className="flex-1"
                        >
                          Skip
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : completed ? (
            <div className="space-y-6">
              <div className="text-center space-y-4">
                <h3 className="text-2xl font-bold">Tes Selesai!</h3>
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-3xl font-bold text-primary">{score}</div>
                      <div className="text-sm text-muted-foreground">Benar</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-3xl font-bold text-secondary">{results.length - score}</div>
                      <div className="text-sm text-muted-foreground">Salah</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-3xl font-bold text-accent-foreground">
                        {Math.round((score / results.length) * 100)}%
                      </div>
                      <div className="text-sm text-muted-foreground">Akurasi</div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Review Jawaban</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {results.map((result, index) => {
                      const bgColor = result.correct ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800';
                      return (
                        <div 
                          key={index} 
                          className={`flex justify-between items-center p-2 rounded ${bgColor}`}
                        >
                          <span>{result.question}</span>
                          <span className="font-mono">
                            {result.userAnswer} {result.correct ? '✅' : '❌'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Button onClick={startTest} className="w-full">
                Coba Lagi
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickTest;